import { DatabaseIcon, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { getCriterionCatalogItem, getCriterionIcon } from "../criterionCatalog";
import {
  type ColumnAggregateScoreMode,
  type ColumnAggregateType,
  type CriterionComparisonValueMode,
  type ScorecardCompareOperator,
  type ScorecardCompareType,
  type ScorecardCountType,
  type TrajectoryExpectationMode,
  type TrajectoryMatchMode,
  buildScorecardPrimitiveConfig,
  buildScorecardSourceColumnIds,
  columnAggregateCanScore,
  columnAggregateUsesExpectedColumn,
  getCodeExecutionSourceColumnIds,
  getContainsComparisonMode,
  getScorecardColumnAggregateScoreMax,
  getScorecardColumnAggregateScoreMode,
  getScorecardColumnAggregateType,
  getScorecardCompareJsonPath,
  getScorecardCompareOperator,
  getScorecardCompareStaticTarget,
  getScorecardCompareTargetMode,
  getScorecardCompareType,
  getScorecardCountBound,
  getScorecardCountType,
  getScorecardExpectedSource,
  getScorecardLabelSource,
  getScorecardPercentile,
  parsePercentile,
  getContainsStaticValue,
  getScorecardAssertValidType,
  getScorecardCode,
  getScorecardCodeLanguage,
  getScorecardPrompt,
  getScorecardPromptSource,
  getScorecardVariableMappings,
  getScorecardRegexPattern,
  getScorecardSourceColumnLabel,
  getTrajectoryExpectedSource,
  getTrajectoryExpectedScenarios,
  getTrajectoryExpectationMode,
  getTrajectoryMatchMode,
  getTrajectoryNoToolsExpected,
  getTrajectoryTraceSource,
  isAgentTracePrimitive,
} from "../criterionConfig";
import {
  buildConfigureScorecardPayload,
  buildConfigureStepsPayload,
  toConfigureStepPayload,
} from "../criterionPayloadUtils";
import { getCriterionMetadataLabel } from "../utils";

import { ScorecardSourceSelector } from "./EditCriterionFields/Inputs/ScorecardSourceSelector";
import EditCriterionFieldsRouter from "./EditCriterionFields";
import {
  extractVariablesFromAssertions,
  normalizeThresholds,
  parseAssertionsFromPrompt,
} from "./EditCriterionFields/EditCriterionView.constants";
import { FailureThresholdField } from "./EditCriterionFields/Layout/FailureThresholdField";
import { Field } from "./EditCriterionFields/Layout/Field";
import { WeightField } from "./EditCriterionFields/Layout/WeightField";

import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import {
  ScorecardPrimitiveType,
  type ConfigureScorecardRequest,
  type Scorecard,
  type ScorecardStep,
  type ScorecardThresholds,
} from "@/queries/smart-tables/scorecard";
import type { SmartColumn } from "@/queries/smart-tables/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface ScorecardEditCriterionViewProps {
  scorecard: Scorecard;
  step: ScorecardStep;
  columns: SmartColumn[];
  isSaving: boolean;
  onSave: (payload: ConfigureScorecardRequest) => void;
  onRemove: (payload: ConfigureScorecardRequest) => void;
  mode?: "edit" | "create";
}

const PERCENT_MULTIPLIER = 100;

const formatThresholdPercent = (threshold: number): string =>
  Number((threshold * PERCENT_MULTIPLIER).toFixed(2)).toString();

const parseOptionalThresholdPercent = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const numericValue = Number(trimmed);
  return Number.isFinite(numericValue) ? numericValue : null;
};

const normalizeThresholdsWithFailurePercent = (
  thresholds: ScorecardThresholds | undefined,
  failureThresholdPercent: string,
): ScorecardThresholds => {
  const normalizedThresholds = normalizeThresholds(thresholds);
  const parsedFailureThreshold = parseOptionalThresholdPercent(failureThresholdPercent);
  if (parsedFailureThreshold === null) {
    return normalizedThresholds;
  }
  return {
    ...normalizedThresholds,
    warn: parsedFailureThreshold / PERCENT_MULTIPLIER,
  };
};

const ScorecardEditCriterionView = ({
  scorecard,
  step,
  columns,
  isSaving,
  onSave,
  onRemove,
  mode = "edit",
}: ScorecardEditCriterionViewProps) => {
  const isCreateMode = mode === "create";
  const Icon = getCriterionIcon(step.primitive_type);
  const criterionTypeLabel =
    getCriterionCatalogItem(step.primitive_type)?.label ?? step.primitive_type.replaceAll("_", " ");
  const sourceColumnOptions = useMemo(
    () => columns.map(column => ({ id: column.id, title: column.title, type: column.type })),
    [columns],
  );
  const codeExecutionSourceColumnIds = useMemo(
    () => getCodeExecutionSourceColumnIds(columns),
    [columns],
  );
  const codeExecutionSourceTitles = useMemo(
    () =>
      columns
        .filter(column => codeExecutionSourceColumnIds.includes(column.id))
        .map(column => column.title),
    [codeExecutionSourceColumnIds, columns],
  );
  const [title, setTitle] = useState(step.title);
  const [sourceColumnId, setSourceColumnId] = useState(() => {
    if (step.primitive_type === ScorecardPrimitiveType.TRAJECTORY) {
      return getTrajectoryTraceSource(step.primitive_config, step.source_column_ids[0] ?? "");
    }
    return typeof step.primitive_config.source === "string"
      ? step.primitive_config.source
      : (step.source_column_ids[0] ?? "");
  });
  const [matchColumnId, setMatchColumnId] = useState(() => {
    if (step.primitive_type === ScorecardPrimitiveType.TRAJECTORY) {
      return getTrajectoryExpectedSource(step.primitive_config) || step.source_column_ids[1] || "";
    }
    return step.source_column_ids[1] ?? "";
  });
  const [comparisonMode, setComparisonMode] = useState<CriterionComparisonValueMode>(() => {
    if (step.primitive_type === ScorecardPrimitiveType.CONTAINS) {
      return getContainsComparisonMode(step.primitive_config);
    }
    if (step.primitive_type === ScorecardPrimitiveType.COMPARE) {
      return getScorecardCompareTargetMode(step.primitive_config);
    }
    return "column";
  });
  const [staticValue, setStaticValue] = useState(() => {
    if (step.primitive_type === ScorecardPrimitiveType.CONTAINS) {
      return getContainsStaticValue(step.primitive_config);
    }
    if (step.primitive_type === ScorecardPrimitiveType.COMPARE) {
      return getScorecardCompareStaticTarget(step.primitive_config);
    }
    return "";
  });
  const [compareType, setCompareType] = useState<ScorecardCompareType>(
    getScorecardCompareType(step.primitive_config),
  );
  const [compareJsonPath, setCompareJsonPath] = useState(
    getScorecardCompareJsonPath(step.primitive_config),
  );
  const [compareOperator, setCompareOperator] = useState<ScorecardCompareOperator>(
    getScorecardCompareOperator(step.primitive_config),
  );
  const initialThresholds = normalizeThresholds(step.thresholds);
  const passThresholdPercent = Number(formatThresholdPercent(initialThresholds.pass));
  const [failureThresholdPercent, setFailureThresholdPercent] = useState(
    formatThresholdPercent(initialThresholds.warn),
  );
  const [weight, setWeight] = useState(String(step.weight ?? 1));
  const [maxCalls, setMaxCalls] = useState(
    String((step.primitive_config.max_calls as number | undefined) ?? 5),
  );
  const initialCodeLanguage = getScorecardCodeLanguage(step.primitive_config);
  const [prompt, setPrompt] = useState(getScorecardPrompt(step.primitive_config));
  const [promptSourceColumnId, setPromptSourceColumnId] = useState(
    getScorecardPromptSource(step.primitive_config),
  );
  const [isLlmAssertionImportMode, setIsLlmAssertionImportMode] = useState(
    getScorecardPromptSource(step.primitive_config).length > 0,
  );
  const [variableMappings, setVariableMappings] = useState<Record<string, string>>(
    getScorecardVariableMappings(step.primitive_config),
  );
  const [code, setCode] = useState(getScorecardCode(step.primitive_config, initialCodeLanguage));
  const [codeLanguage, setCodeLanguage] = useState(initialCodeLanguage);
  const [regexPattern, setRegexPattern] = useState(getScorecardRegexPattern(step.primitive_config));
  const [assertValidType, setAssertValidType] = useState(
    getScorecardAssertValidType(step.primitive_config),
  );
  const [countType, setCountType] = useState<ScorecardCountType>(
    getScorecardCountType(step.primitive_config),
  );
  const [minCount, setMinCount] = useState(
    getScorecardCountBound(step.primitive_config, "min_count"),
  );
  const [maxCount, setMaxCount] = useState(
    getScorecardCountBound(step.primitive_config, "max_count"),
  );
  const [trajectoryExpectationMode, setTrajectoryExpectationMode] =
    useState<TrajectoryExpectationMode>(getTrajectoryExpectationMode(step.primitive_config));
  const [expectedScenarios, setExpectedScenarios] = useState(
    getTrajectoryExpectedScenarios(step.primitive_config),
  );
  const [trajectoryNoToolsExpected, setTrajectoryNoToolsExpected] = useState(
    () => !isCreateMode && getTrajectoryNoToolsExpected(step.primitive_config),
  );
  const [trajectoryMatchMode, setTrajectoryMatchMode] = useState<TrajectoryMatchMode>(
    getTrajectoryMatchMode(step.primitive_config),
  );
  const initialColumnAggregateType = getScorecardColumnAggregateType(step.primitive_config);
  const initialSourceColumn = columns.find(
    column =>
      column.id ===
      (typeof step.primitive_config.source === "string"
        ? step.primitive_config.source
        : (step.source_column_ids[0] ?? "")),
  );
  const [columnAggregateType, setColumnAggregateType] = useState<ColumnAggregateType>(
    initialSourceColumn?.type === "TEXT" && initialColumnAggregateType === "average_value"
      ? "most_frequent_value"
      : initialColumnAggregateType,
  );
  const [expectedColumnId, setExpectedColumnId] = useState(
    getScorecardExpectedSource(step.primitive_config),
  );
  const [percentile, setPercentile] = useState(getScorecardPercentile(step.primitive_config));
  const [columnAggregateScoreMode, setColumnAggregateScoreMode] =
    useState<ColumnAggregateScoreMode>(getScorecardColumnAggregateScoreMode(step.primitive_config));
  const [columnAggregateScoreMax, setColumnAggregateScoreMax] = useState(
    getScorecardColumnAggregateScoreMax(step.primitive_config),
  );
  const [labelColumnId, setLabelColumnId] = useState(
    getScorecardLabelSource(step.primitive_config),
  );
  const isColumnAggregate = step.primitive_type === ScorecardPrimitiveType.COLUMN_AGGREGATE;
  const isContains = step.primitive_type === ScorecardPrimitiveType.CONTAINS;
  const isCompare = step.primitive_type === ScorecardPrimitiveType.COMPARE;
  const isMaxToolCalls = step.primitive_type === ScorecardPrimitiveType.MAX_TOOL_CALLS;
  const isCount = step.primitive_type === ScorecardPrimitiveType.COUNT;
  const isTrajectory = step.primitive_type === ScorecardPrimitiveType.TRAJECTORY;
  const isLlmAssertion = step.primitive_type === ScorecardPrimitiveType.LLM_ASSERTION;
  const isCodeExecution = step.primitive_type === ScorecardPrimitiveType.CODE_EXECUTION;
  const isRegex = step.primitive_type === ScorecardPrimitiveType.REGEX;
  const isAssertValid = step.primitive_type === ScorecardPrimitiveType.ASSERT_VALID;
  const isAgentBehavior = isAgentTracePrimitive(step.primitive_type);
  const detectedPromptVariables = useMemo(
    () =>
      isLlmAssertion && !isLlmAssertionImportMode ? extractVariablesFromAssertions(prompt) : [],
    [isLlmAssertion, isLlmAssertionImportMode, prompt],
  );
  const assertions = useMemo(() => parseAssertionsFromPrompt(prompt), [prompt]);
  const normalizedVariableMappings = useMemo(
    () =>
      detectedPromptVariables.reduce<Record<string, string>>((acc, variable) => {
        const columnId = variableMappings[variable];
        if (columnId) {
          acc[variable] = columnId;
        }
        return acc;
      }, {}),
    [detectedPromptVariables, variableMappings],
  );
  const hasUnmappedLlmVariables =
    isLlmAssertion &&
    !isLlmAssertionImportMode &&
    detectedPromptVariables.some(variable => !variableMappings[variable]);
  const hasMissingLlmAssertionSource = isLlmAssertion && !sourceColumnId;
  const hasMissingLlmPromptSource =
    isLlmAssertion && isLlmAssertionImportMode && !promptSourceColumnId;
  const hasMissingLlmAssertions =
    isLlmAssertion && !isLlmAssertionImportMode && assertions.every(assertion => !assertion.trim());
  const handleAssertionsChange = useCallback((nextAssertions: string[]) => {
    setPrompt(JSON.stringify(nextAssertions));
  }, []);
  const handleColumnAggregateTypeChange = (nextType: ColumnAggregateType) => {
    setColumnAggregateType(nextType);
    if (nextType !== "min_value" && nextType !== "max_value") {
      setLabelColumnId("");
    }
    if (!columnAggregateCanScore(nextType)) {
      setColumnAggregateScoreMode("value");
    }
  };
  const handleSourceColumnChange = (nextColumnId: string) => {
    setSourceColumnId(nextColumnId);
    const nextColumn = columns.find(column => column.id === nextColumnId);
    if (isCreateMode && isColumnAggregate && nextColumn && title === step.title) {
      setTitle(nextColumn.title);
    }
    if (!isColumnAggregate) {
      return;
    }

    // Average, min and max need a numeric column; the other types read text cells themselves.
    if (
      nextColumn?.type === "TEXT" &&
      (columnAggregateType === "average_value" ||
        columnAggregateType === "min_value" ||
        columnAggregateType === "max_value")
    ) {
      setColumnAggregateType("most_frequent_value");
      setLabelColumnId("");
    }
  };
  const handleCompareTypeChange = (nextType: ScorecardCompareType) => {
    setCompareType(nextType);
    if (nextType === "JSON" && !compareJsonPath) {
      setCompareJsonPath("$.*");
    }
  };
  const metadataLabel = isColumnAggregate
    ? getCriterionMetadataLabel(
        {
          primitive_type: step.primitive_type,
          source_column_ids: [sourceColumnId],
          primitive_config: { ...step.primitive_config, source: sourceColumnId },
        },
        columns,
        { isColumnAggregate: true },
      )
    : null;
  const scoringBehaviorDescription = (() => {
    if (isColumnAggregate) {
      return "Column evaluator — produces one scorecard-wide result from the selected column.";
    }
    if (isAgentBehavior) {
      return "Pass/fail check — scores 100% when agent behavior satisfies the rule.";
    }
    return "Pass/fail check — scores 100% when the rule holds, 0% when it breaks.";
  })();
  const scoreContributionLabels: Partial<Record<ColumnAggregateType, string>> = {
    average_value: "Use normalized average as score",
    classification_report: "Use accuracy as score",
  };
  const scoreContributionLabel =
    scoreContributionLabels[columnAggregateType] ?? "Use winning share as score";
  const scoreContributionHelper = (() => {
    if (columnAggregateScoreMode !== "score") {
      return "Shows the result without affecting the scorecard's overall score.";
    }
    if (columnAggregateType === "average_value") {
      return "Values are divided by a maximum so the score bar stays on a 0–100% scale.";
    }
    if (columnAggregateType === "classification_report") {
      return "Score is accuracy.";
    }
    return "The score is the percentage of rows with the most common value.";
  })();

  const llmAssertionConfig = (() => {
    if (!isLlmAssertion) {
      return {};
    }
    if (isLlmAssertionImportMode) {
      return {
        source: sourceColumnId,
        prompt_source: promptSourceColumnId,
      };
    }
    return {
      prompt,
      source: sourceColumnId,
      variable_mappings: normalizedVariableMappings,
    };
  })();

  const buildUpdatedStep = (): ScorecardStep => {
    const existingConfig = {
      ...step.primitive_config,
      ...llmAssertionConfig,
      ...(isCodeExecution ? { code, language: codeLanguage } : {}),
      ...(isRegex ? { regex_pattern: regexPattern } : {}),
      ...(isAssertValid ? { type: assertValidType } : {}),
    };
    const trajectoryMatchColumnId =
      isTrajectory && trajectoryExpectationMode === "column" ? matchColumnId : undefined;
    let configMatchColumnId: string | undefined = matchColumnId;
    if (isTrajectory) {
      configMatchColumnId = trajectoryMatchColumnId;
    } else if (isColumnAggregate) {
      configMatchColumnId = columnAggregateUsesExpectedColumn(columnAggregateType)
        ? expectedColumnId
        : labelColumnId;
    }
    const primitiveConfig = buildScorecardPrimitiveConfig({
      primitiveType: step.primitive_type,
      sourceColumnId,
      matchColumnId: isTrajectory ? trajectoryMatchColumnId : matchColumnId,
      staticValue,
      comparisonMode,
      maxCalls: Number(maxCalls) || 5,
      aggregateType: columnAggregateType,
      aggregateScoreMode: columnAggregateScoreMode,
      aggregateScoreMax: columnAggregateScoreMax,
      labelColumnId,
      expectedColumnId,
      percentile,
      compareType,
      compareJsonPath,
      compareOperator,
      variableMappings:
        isLlmAssertion && !isLlmAssertionImportMode ? normalizedVariableMappings : undefined,
      promptSourceColumnId: isLlmAssertionImportMode ? promptSourceColumnId : undefined,
      countType,
      minCount,
      maxCount,
      trajectoryExpectationMode,
      expectedScenarios,
      trajectoryNoToolsExpected,
      trajectoryMatchMode,
      existingConfig,
    });
    return {
      ...step,
      title,
      primitive_type: step.primitive_type,
      weight: Number(weight) || 1,
      thresholds: normalizeThresholdsWithFailurePercent(step.thresholds, failureThresholdPercent),
      source_column_ids: buildScorecardSourceColumnIds({
        primitiveType: step.primitive_type,
        sourceColumnId,
        matchColumnId: configMatchColumnId,
        comparisonMode,
        codeExecutionSourceColumnIds,
        variableMappings:
          isLlmAssertion && !isLlmAssertionImportMode ? normalizedVariableMappings : undefined,
        promptSourceColumnId: isLlmAssertionImportMode ? promptSourceColumnId : undefined,
      }),
      primitive_config: primitiveConfig,
    };
  };

  const handleSave = () => {
    const updatedStep = buildUpdatedStep();
    const steps = isCreateMode
      ? [
          ...scorecard.steps.map(existingStep => toConfigureStepPayload(existingStep)),
          toConfigureStepPayload(updatedStep, {
            orderIndex: scorecard.steps.length,
            includeId: false,
          }),
        ]
      : buildConfigureStepsPayload(scorecard, updatedStep);

    onSave(buildConfigureScorecardPayload(scorecard, steps));
  };

  const handleRemove = () => {
    onRemove(
      buildConfigureScorecardPayload(
        scorecard,
        buildConfigureStepsPayload(scorecard, step, step.id),
      ),
    );
  };

  let submitLabel = "Save changes";
  if (isCreateMode) {
    submitLabel = "Add evaluator";
  }
  if (isSaving) {
    submitLabel = isCreateMode ? "Adding…" : "Saving…";
  }

  const hasMissingNumberStaticTarget =
    isCompare && compareType === "NUMBER" && comparisonMode === "static" && !staticValue.trim();
  const hasInvalidCompareConfig =
    (isCompare && compareType === "JSON" && !compareJsonPath.trim()) ||
    hasMissingNumberStaticTarget;
  const hasMissingColumnEvaluatorSource =
    isColumnAggregate &&
    (!sourceColumnId ||
      (columnAggregateUsesExpectedColumn(columnAggregateType) && !expectedColumnId) ||
      (columnAggregateType === "percentile_value" && parsePercentile(percentile) === undefined));
  const parsedMinCount = minCount.trim() ? Number(minCount) : undefined;
  const parsedMaxCount = maxCount.trim() ? Number(maxCount) : undefined;
  const hasInvalidCountBounds =
    isCount &&
    ((!minCount.trim() && !maxCount.trim()) ||
      (minCount.trim().length > 0 &&
        (!Number.isInteger(parsedMinCount) || (parsedMinCount ?? -1) < 0)) ||
      (maxCount.trim().length > 0 &&
        (!Number.isInteger(parsedMaxCount) || (parsedMaxCount ?? -1) < 0)) ||
      (typeof parsedMinCount === "number" &&
        typeof parsedMaxCount === "number" &&
        parsedMinCount > parsedMaxCount));
  const hasInvalidTrajectoryExpectation =
    isTrajectory &&
    ((trajectoryExpectationMode === "tools" &&
      !trajectoryNoToolsExpected &&
      !expectedScenarios.some(scenario => scenario.some(tool => tool.trim()))) ||
      (trajectoryExpectationMode === "column" && !matchColumnId));
  const compareStaticPlaceholder =
    compareType === "NUMBER" ? "Enter a number, like 0.8" : "Enter the expected value…";
  const compareStaticDescription =
    compareType === "NUMBER"
      ? "Parses this fixed target as a number before applying the selected operator."
      : "Uses this fixed value as the target for every row.";
  const parsedFailureThresholdPercent = parseOptionalThresholdPercent(failureThresholdPercent);
  const hasInvalidFailureThreshold =
    failureThresholdPercent.trim().length > 0 &&
    (parsedFailureThresholdPercent === null ||
      parsedFailureThresholdPercent < 0 ||
      parsedFailureThresholdPercent > passThresholdPercent);

  return (
    <div className="space-y-5">
      <div className="space-y-3 border-b border-border/60 pb-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {isCreateMode ? "New Evaluator" : "Edit Evaluator"}
        </p>
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted/60 text-foreground">
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-lg font-semibold text-foreground">{criterionTypeLabel}</h3>
            {metadataLabel ? (
              <p className="mt-0.5 truncate text-xs text-muted-foreground">{metadataLabel}</p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <Field label="Evaluator name">
          <Input value={title} onChange={event => setTitle(event.target.value)} />
        </Field>

        {!isCodeExecution && !isLlmAssertion ? (
          <SectionGroup
            title="Data Source"
            description="Select the data to evaluate"
            icon={<DatabaseIcon className="h-4 w-4" />}
          >
            <ScorecardSourceSelector
              value={sourceColumnId}
              onValueChange={handleSourceColumnChange}
              placeholder="Select a source..."
              options={sourceColumnOptions}
              name={getScorecardSourceColumnLabel(step.primitive_type)}
            />
          </SectionGroup>
        ) : null}

        <EditCriterionFieldsRouter
          isColumnAggregate={isColumnAggregate}
          isContains={isContains}
          isCompare={isCompare}
          isMaxToolCalls={isMaxToolCalls}
          isCount={isCount}
          isTrajectory={isTrajectory}
          isLlmAssertion={isLlmAssertion}
          isCodeExecution={isCodeExecution}
          isRegex={isRegex}
          isAssertValid={isAssertValid}
          columnAggregateType={columnAggregateType}
          onColumnAggregateTypeChange={handleColumnAggregateTypeChange}
          columnAggregateScoreMode={columnAggregateScoreMode}
          onColumnAggregateScoreModeChange={setColumnAggregateScoreMode}
          columnAggregateScoreMax={columnAggregateScoreMax}
          onColumnAggregateScoreMaxChange={setColumnAggregateScoreMax}
          labelColumnId={labelColumnId}
          onLabelColumnIdChange={setLabelColumnId}
          expectedColumnId={expectedColumnId}
          onExpectedColumnIdChange={setExpectedColumnId}
          percentile={percentile}
          onPercentileChange={setPercentile}
          sourceColumnId={sourceColumnId}
          onSourceColumnChange={handleSourceColumnChange}
          sourceColumnOptions={sourceColumnOptions}
          scoreContributionLabel={scoreContributionLabel}
          scoreContributionHelper={scoreContributionHelper}
          comparisonMode={comparisonMode}
          staticValue={staticValue}
          matchColumnId={matchColumnId}
          columns={columns}
          onComparisonModeChange={setComparisonMode}
          onStaticValueChange={setStaticValue}
          onMatchColumnIdChange={setMatchColumnId}
          compareStaticPlaceholder={compareStaticPlaceholder}
          compareStaticDescription={compareStaticDescription}
          hasMissingNumberStaticTarget={hasMissingNumberStaticTarget}
          compareType={compareType}
          onCompareTypeChange={handleCompareTypeChange}
          compareOperator={compareOperator}
          onCompareOperatorChange={setCompareOperator}
          compareJsonPath={compareJsonPath}
          onCompareJsonPathChange={setCompareJsonPath}
          hasInvalidCompareConfig={hasInvalidCompareConfig}
          maxCalls={maxCalls}
          onMaxCallsChange={setMaxCalls}
          assertions={assertions}
          detectedPromptVariables={detectedPromptVariables}
          onAssertionsChange={handleAssertionsChange}
          isLlmAssertionImportMode={isLlmAssertionImportMode}
          onLlmAssertionImportModeChange={setIsLlmAssertionImportMode}
          promptSourceColumnId={promptSourceColumnId}
          onPromptSourceColumnChange={setPromptSourceColumnId}
          variableMappings={variableMappings}
          onVariableMappingsChange={setVariableMappings}
          hasUnmappedLlmVariables={hasUnmappedLlmVariables}
          code={code}
          codeLanguage={codeLanguage}
          codeExecutionSourceTitles={codeExecutionSourceTitles}
          onCodeChange={setCode}
          onCodeLanguageChange={setCodeLanguage}
          regexPattern={regexPattern}
          onRegexPatternChange={setRegexPattern}
          assertValidType={assertValidType}
          onAssertValidTypeChange={setAssertValidType}
          countType={countType}
          onCountTypeChange={setCountType}
          minCount={minCount}
          onMinCountChange={setMinCount}
          maxCount={maxCount}
          onMaxCountChange={setMaxCount}
          hasInvalidCountBounds={hasInvalidCountBounds}
          trajectoryExpectationMode={trajectoryExpectationMode}
          onTrajectoryExpectationModeChange={setTrajectoryExpectationMode}
          expectedScenarios={expectedScenarios}
          onExpectedScenariosChange={setExpectedScenarios}
          trajectoryNoToolsExpected={trajectoryNoToolsExpected}
          onTrajectoryNoToolsExpectedChange={setTrajectoryNoToolsExpected}
          expectedSourceColumnId={matchColumnId}
          onExpectedSourceColumnChange={setMatchColumnId}
          trajectoryMatchMode={trajectoryMatchMode}
          onTrajectoryMatchModeChange={setTrajectoryMatchMode}
          hasInvalidTrajectoryExpectation={hasInvalidTrajectoryExpectation}
          traceSourceColumnId={sourceColumnId}
        />

        <div className="rounded-lg border border-border/70 bg-muted/10 px-3 py-3 text-sm text-muted-foreground">
          {scoringBehaviorDescription}
        </div>

        <FailureThresholdField
          value={failureThresholdPercent}
          onValueChange={setFailureThresholdPercent}
          passThresholdPercent={passThresholdPercent}
        />

        {hasInvalidFailureThreshold ? (
          <p className="-mt-2 text-xs text-red-600">
            Enter a percentage from 0 to {passThresholdPercent}.
          </p>
        ) : null}

        <WeightField weight={weight} onWeightChange={setWeight} />
      </div>

      <div className="flex items-center justify-between gap-3">
        {!isCreateMode ? (
          <Button
            variant="ghost"
            className="text-red-600 hover:text-red-700"
            onClick={handleRemove}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Remove evaluator
          </Button>
        ) : (
          <div />
        )}
        <Button
          onClick={handleSave}
          disabled={
            isSaving ||
            hasUnmappedLlmVariables ||
            hasMissingLlmAssertionSource ||
            hasMissingLlmPromptSource ||
            hasMissingLlmAssertions ||
            hasMissingColumnEvaluatorSource ||
            hasInvalidCompareConfig ||
            hasInvalidCountBounds ||
            hasInvalidTrajectoryExpectation ||
            hasInvalidFailureThreshold
          }
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default ScorecardEditCriterionView;
